import { useRef, useEffect, useState } from 'react';
import {
    useCarBySlug,
    useLogEntries,
} from '../../hooks/useCarData';
import { RepairAnalysis } from '../../views/RepairAnalysis';
import { NavigationBar } from '../ui/NavigationBar';
import { HealthCircle } from '../ui/HealthCircle';
import { Menu } from 'lucide-react';

interface RepairLandingPreviewProps {
    slug: string;
}

// Virtual render width — matches CarLandingPreview for identical "phone" look
const RENDER_WIDTH = 430;

/**
 * Renders the Repair Analysis view inside a simulated phone frame for the landing page.
 */
export function RepairLandingPreview({ slug }: RepairLandingPreviewProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.71); 

    const { data: car, isLoading: carLoading } = useCarBySlug(slug);
    const { data: logEntries = [] } = useLogEntries(car?.id);
    const [healthStatus, setHealthStatus] = useState({ score: 0, recommendation: '' });

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width;
            if (w > 0) setScale(w / RENDER_WIDTH);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="w-full h-full overflow-hidden relative">
            {!car || carLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
                </div>
            ) : (
                <div
                    style={{
                        width: `${RENDER_WIDTH}px`,
                        transformOrigin: 'top left',
                        transform: `scale(${scale})`,
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                >
                    <NavigationBar
                        title="Repair Analysis"
                        leftAction={{
                            icon: Menu,
                            onClick: () => {},
                            className: 'text-blue-600 dark:text-blue-400',
                        }}
                        fixed={false}
                        rightContent={
                            <div className="flex items-center bg-blue-600/5 dark:bg-white/5 p-1 rounded-full border border-blue-600/5 dark:border-white/5 shadow-sm">
                               <HealthCircle 
                                 score={healthStatus.score} 
                                 size={40} 
                                 strokeWidth={4} 
                                 innerRadius={14} 
                                 showText={true} 
                                 fontSize="11px" 
                               />
                            </div>
                        }
                    />
                    <div className="bg-gradient-theme min-h-[800px]">
                        <RepairAnalysis 
                            car={car} 
                            logEntries={logEntries} 
                            isReadOnly={true} 
                            initialView="health" 
                            onStatusUpdate={setHealthStatus}
                            compact={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
